import { z } from 'zod';

import type {
  AccountType,
  FxRateEvidenceContext,
  NaturalBalance,
  RollupCategory,
} from '../generated/enums.js';
import type { LedgerViewPresetResponse } from './ledger-view-presets.js';

// GL Balances – wire contracts

export interface ListBalancesQuery {
  entityId?: string;
  bookId?: string;
  periodId?: string;
  includeNetBalances?: boolean;
  presetId?: string;
}

export interface ListFoldedBalanceChildrenQuery extends ListBalancesQuery {
  rootAccountId: string;
}

export interface BalanceRowResponse {
  entityId: string;
  bookId: string;
  periodId: string;
  glAccountId: string;
  opening: string;
  debits: string;
  credits: string;
  closing: string;
  netBalance?: string | null;
  importRunId?: string | null;
  createdAt: string;
  accountType: AccountType;
  normalSign: 1 | -1;
  /** Optional richer account metadata for reporting (Balancete, exports) */
  accountCode?: string;
  accountName?: string;
  /** Optional rollup grouping metadata for reporting */
  rollupGroupId?: string;
  rollupGroupCode?: string;
  rollupGroupName?: string;
  rollupCategory?: RollupCategory;
  naturalBalance?: NaturalBalance;
  isPostable?: boolean;
  /** Presentation side for opening/closing balances (D/C), if applicable */
  openingSide?: 'D' | 'C' | null;
  closingSide?: 'D' | 'C' | null;
  /** Movements may be redacted while opening/closing balances remain visible. */
  movementVisibility?: 'FULL' | 'REDACTED';
  /** True when generated subject children have been projected into this folded root. */
  isFolded?: boolean;
  /** Number of visible generated subject children represented by this folded root. */
  foldedChildrenCount?: number;
  /** Stable account ids available through the folded-root children endpoint. */
  foldedChildAccountIds?: string[];
  /** Preset resolved for this scoped reading, or null for the detailed reading. */
  resolvedPreset?: LedgerViewPresetResponse | null;
}

// GL Ledger (Razão) – wire contracts

export type LedgerMovementFilter = 'all' | 'withMovement' | 'withoutMovement';

export type LedgerOrderByField = 'account' | 'date' | 'debit' | 'credit' | 'balance';
export type LedgerRowMode = 'grouped' | 'lines';

export interface ListLedgerQuery {
  entityId: string;
  bookId: string;
  periodId: string;
  /** Preset UUID, or `none` to force the detailed reading. */
  presetId?: string;
  /** Row layout for a preset reading. Ignored when no preset is active. */
  rowMode?: LedgerRowMode;
  /** Optional direct account reference (takes precedence over accountCode when both provided) */
  glAccountId?: string;
  /** Optional account code (resolved within the CoA version linked to the period) */
  accountCode?: string;
  /** Optional movement filter when listing multiple accounts in the ledger */
  movementFilter?: LedgerMovementFilter;
  includeDescendants?: boolean;
  /** Optional filter by event source (e.g. ap_bill, ar_invoice). When set, only lines with matching sourceTable are returned. */
  sourceTable?: string;
  /** Optional exact posting group. Used by record deep links to open only the selected journal. */
  sourceTransactionId?: string;
  /** Optional date range filter (ISO date strings yyyy-MM-dd). Applied within the period. */
  fromDate?: string;
  /** Optional date range filter (ISO date strings yyyy-MM-dd). Applied within the period. */
  toDate?: string;
  /** Optional minimum amount filter (line amount = max(debit, credit)). */
  minAmount?: number;
  /** Optional maximum amount filter (line amount = max(debit, credit)). */
  maxAmount?: number;
  /** One-based grouped-posting page. Applies only when a ledger view preset is active. */
  page?: number;
  /** Grouped postings per page. Applies only when a ledger view preset is active. */
  pageSize?: number;
}

/**
 * Query parameters for fetching all GL origin events related to a given
 * operational object (e.g. AP bill, AR invoice, HCM event) identified by
 * sourceTable + sourceId.
 */
export interface ListOriginEventsQuery {
  /** Optional entity guard when the same sourceId could exist in multiple entities */
  entityId?: string;
  sourceTable: string;
  sourceId: string;
}

export interface LedgerLineResponse {
  journalId: string;
  journalDate: string;
  documentType: string | null;
  documentNumber: string | null;
  memo: string | null;
  debit: string;
  credit: string;
  trxCurrency?: string | null;
  trxAmount?: string | null;
  functionalAmount?: string | null;
  fxRate?: string | null;
  fxRoundingDelta?: string | null;
  fxRateEvidenceId?: string | null;
  /** Running balance after this line, in the account's natural direction */
  balance: string;
  /** Known reversal date when the reversing journal is outside the visible range. */
  reversalDate?: string | null;
  sourceTable?: string | null;
  sourceId?: string | null;
  counterpartyId?: string | null;
  glAccountId?: string;
  accountCode?: string | null;
  accountName?: string | null;
}

export interface LedgerAccountInfo {
  glAccountId: string;
  code: string;
  name: string;
  accountType: AccountType;
  naturalBalance: NaturalBalance;
}

export interface LedgerAccountLedger {
  account: LedgerAccountInfo;
  /** Opening balance at the start of the period (for now, period-level opening) */
  openingBalance: string;
  lines: LedgerLineResponse[];
  totalDebit: string;
  totalCredit: string;
  /** Closing balance at the end of the period (opening + movements) */
  closingBalance: string;
}

/**
 * Lightweight view of journal lines used when presenting origin events tied to
 * a specific sourceTable/sourceId (e.g. all GL movements linked to a given AP
 * bill). This is intentionally flatter than LedgerLineResponse and focused on
 * event storytelling rather than per-account running balances.
 */
export interface OriginEventLineResponse {
  journalId: string;
  journalDate: string;
  documentType: string | null;
  documentNumber: string | null;
  memo: string | null;
  glAccountId: string;
  accountCode: string;
  accountName: string;
  debit: string;
  credit: string;
  sourceTable?: string | null;
  sourceId?: string | null;
  /** Presentation-only: URL the UI should navigate to when drilling down into the origin.
   *  Backend-resolved from (sourceTable, sourceId). Null when no handler exists. */
  originUrl?: string | null;
}

/**
 * High-level AI-generated summary for a set of GL origin events, used by
 * the Razão (ledger) drilldown panel. The content is presented to users in
 * Portuguese and is intended for human consumption only.
 */
export interface GlLedgerIaSummarySection {
  /** Stable identifier used by the UI/layout, e.g. 'patterns', 'anomalies', 'composition'. */
  key: string;
  /** Short label shown in bold before the section body, e.g. 'Padrões'. */
  label: string;
  /** Free-form text in pt-BR describing this aspect of the events. */
  body: string;
}

export interface GlLedgerIaSummary {
  /** Title for the summary card, typically 'Resumo IA'. */
  title: string;
  /** Ordered sections matching the UI layout (Padrões, Anomalias, Composição, etc.). */
  sections: GlLedgerIaSummarySection[];
}

export const GlLedgerIaSummarySectionSchema = z.object({
  key: z.string(),
  label: z.string(),
  body: z.string(),
});

export const GlLedgerIaSummarySchema = z.object({
  title: z.string(),
  sections: z.array(GlLedgerIaSummarySectionSchema),
});

/**
 * Response contract for GL origin events. The AI summary for a set of events
 * is exposed via a separate /gl/ledger/ai-summary endpoint.
 */
export interface OriginEventsResponse {
  events: OriginEventLineResponse[];
}

/**
 * Request payload for generating an AI summary for a set of GL ledger
 * events. The caller is responsible for providing the flattened
 * `OriginEventLineResponse` array that represents the current drilldown
 * context (e.g. all movements for a given account/selectionMode).
 */
export interface GlLedgerIaSummaryRequest {
  events: OriginEventLineResponse[];
}

export interface LedgerResponse {
  entityId: string;
  bookId: string;
  periodId: string;
  /** Preset resolved for this reading, or null for the detailed reading. */
  resolvedPreset: LedgerViewPresetResponse | null;
  /** Ledger detail grouped by account. When a specific account is requested, this will contain a single element. */
  accounts: LedgerAccountLedger[];
  /** Optional AI-generated summary for the current ledger view (single-account drilldown). */
  summary?: GlLedgerIaSummary | null;
  /** Read-time projected rows. Present only when a ledger view preset is active. */
  groupedPostings?: LedgerGroupedPostingResponse[];
  groupedPostingPage?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface LedgerGroupedPostingLineResponse {
  glAccountId: string;
  code: string;
  name: string;
  debitTotal: string;
  creditTotal: string;
}

export interface LedgerGroupedPostingResponse {
  groupedPostingId: string;
  date: string;
  postingSource: string | null;
  documentCount: number;
  lines: LedgerGroupedPostingLineResponse[];
  memberJournalIds: string[];
  reversalDate: string | null;
  isReversal: boolean;
  /** Running balance after this grouped posting in single-account ledger reads. */
  runningBalance: string | null;
}

export interface LedgerGroupedPostingMemberResponse {
  journalId: string;
  date: string;
  documentType: string;
  documentNumber: string | null;
  subjectName: string | null;
  functionalAmount: string;
  documentCurrency: string | null;
  documentAmount: string | null;
}

export interface LedgerGroupedPostingDrillDownResponse {
  groupedPostingId: string;
  members: LedgerGroupedPostingMemberResponse[];
}

export interface JournalLineResponse {
  journalLineId: string;
  journalId: string;
  glAccountId: string;
  description: string;
  debitAmount: string;
  creditAmount: string;
  reportingAmount?: string | null;
  trxCurrency?: string | null;
  trxAmount?: string | null;
  functionalAmount?: string | null;
  fxRate?: string | null;
  fxRoundingDelta?: string | null;
  fxRateEvidenceId?: string | null;
  counterpartyId?: string | null;
  segmentJson?: string | null;
  sourceTable?: string | null;
  sourceId?: string | null;
  reference?: string | null;
  classificationId?: string | null;
  splitId?: string | null;
  costCenterId?: string | null;
  departmentId?: string | null;
  managerialAccountId?: string | null;
  isOffset?: boolean;
  sourceJournalLineId?: string | null;
  createdAt: string;
}

// GL Journals – request contracts

export interface CreateJournalLineRequest {
  glAccountId: string;
  description: string;
  debitAmount: number;
  creditAmount: number;
  /** Optional amount in reporting currency; calculated if omitted */
  reportingAmount?: number;
  /** Currency of the source economic event. Required for new hard-reject postings. */
  trxCurrency?: string;
  /** Signed source amount, serialized as a decimal string. */
  trxAmount?: string;
  /** Signed functional amount, serialized as a decimal string. */
  functionalAmount?: string;
  /** Positive immutable conversion rate, serialized as a decimal string. */
  fxRate?: string;
  /** Immutable rate evidence for converted lines. */
  fxRateEvidenceId?: string;
  /** Expected context of the immutable evidence for a converted line. */
  fxRateEvidenceContext?: FxRateEvidenceContext;
  /** Exact rounding delta produced by the conversion. */
  fxRoundingDelta?: string;
  segmentJson?: string;
  sourceTable?: string;
  sourceId?: string;
  reference?: string;
  classificationId?: string;
  splitId?: string;
  costCenterId?: string;
  departmentId?: string;
  managerialAccountId?: string;
}

export interface CreateJournalRequest {
  entityId: string;
  bookId: string;
  periodId: string;
  /** ISO-8601 date string */
  journalDate: string;
  description: string;
  reference?: string;
  /** Stable producer identifier used by currency-enforcement rollout reporting. */
  postingSource?: string;
  /** Team or service responsible for remediating a rollout violation. */
  postingOwner?: string;
  lines: CreateJournalLineRequest[];
}

export interface PostBookJournalRequest {
  bookId: string;
  periodId: string;
  lines: CreateJournalLineRequest[];
}

export interface PostJournalBatchRequest {
  entityId: string;
  /** ISO-8601 date string */
  journalDate: string;
  description: string;
  reference?: string;
  /** Stable producer identifier used by currency-enforcement rollout reporting. */
  postingSource?: string;
  /** Team or service responsible for remediating a rollout violation. */
  postingOwner?: string;
  books: PostBookJournalRequest[];
}
